import type { ForceElement } from "@/types/force";
import type { TrainingTier } from "@/types/game-data";

/**
 * Represents a group of identical models within an element.
 * Models are considered identical if they share the same role (soldier),
 * training tier, sorted weapon IDs, and sorted equipment IDs.
 */
export interface ModelGroup {
	/** Number of models in this group */
	count: number;
	/** Role: commander, sergeant, or soldier */
	role: "commander" | "sergeant" | "soldier";
	/** Training tier of these models (same as element tier) */
	tier: TrainingTier;
	/** Sorted weapon IDs equipped by each model in this group */
	weaponIds: string[];
	/** Sorted equipment IDs equipped by each model in this group */
	equipmentIds: string[];
	/** Display label, e.g. "3x Tier 2 Soldier" */
	label: string;
}

/** Format a tier name for display */
function formatTier(tier: TrainingTier): string {
	switch (tier) {
		case "tier1": return "Tier 1";
		case "tier2": return "Tier 2";
		case "veteran": return "Veteran";
		case "enlisted": return "Enlisted";
		case "militia": return "Militia";
		case "conscript": return "Conscript";
		case "criminal": return "Criminal";
		default: return tier;
	}
}

/** Format a role name for display */
function formatRole(role: "commander" | "sergeant" | "soldier"): string {
	switch (role) {
		case "commander": return "Commander";
		case "sergeant": return "Sergeant";
		case "soldier": return "Soldier";
	}
}

/**
 * Group models within a ForceElement by their profile.
 * Commanders and Sergeants always get their own group (never merged with soldiers).
 * Regular soldiers with identical loadouts are grouped together.
 *
 * @param element - The ForceElement whose models to group
 * @returns Array of ModelGroup objects
 */
export function groupModelsByProfile(element: ForceElement): ModelGroup[] {
	const groups: ModelGroup[] = [];
	const soldierBuckets = new Map<string, ModelGroup>();

	for (const model of element.models) {
		const sortedWeapons = [...model.weapons].sort();
		const sortedEquipment = [...model.equipment].sort();

		// Commanders and Sergeants always get their own entry
		if (model.role === "commander" || model.role === "sergeant") {
			groups.push({
				count: 1,
				role: model.role,
				tier: element.tier,
				weaponIds: sortedWeapons,
				equipmentIds: sortedEquipment,
				label: `${formatTier(element.tier)} ${formatRole(model.role)}`,
			});
			continue;
		}

		// Regular soldiers — group by loadout key
		const key = `${sortedWeapons.join(",")}|${sortedEquipment.join(",")}`;
		const existing = soldierBuckets.get(key);
		if (existing) {
			existing.count += 1;
			// Update label with new count
			existing.label = `${existing.count}x ${formatTier(element.tier)} ${formatRole(model.role)}`;
		} else {
			const group: ModelGroup = {
				count: 1,
				role: "soldier",
				tier: element.tier,
				weaponIds: sortedWeapons,
				equipmentIds: sortedEquipment,
				label: `1x ${formatTier(element.tier)} ${formatRole("soldier")}`,
			};
			soldierBuckets.set(key, group);
		}
	}

	// Add soldier groups after the special roles
	for (const group of soldierBuckets.values()) {
		groups.push(group);
	}

	return groups;
}
